import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { focusManager, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import {
  initialLocalStorageFilterValues,
  initialLocationFilterValues,
  LocalStorageFilterValueTypes,
  LocationFilterValueTypes,
} from "src/components/molecules/FilterBar/types";
import { Alert, Colors, useFeatureFlags } from "swing-components";

import {
  ContentSingleColumn,
  ExpiringDocumentsBanner,
  FilterBar,
  LocationFilterBar,
  NonSwingCertCard,
  NonSwingCertEmptyView,
  OpeningsCard,
  useAuth,
  useExpiringDocuments,
} from "~components";
import {
  areFiltersSet,
  GET,
  OpeningsData,
  PendingConfirmData,
  translate,
  useLocalStorage,
} from "~utils";

import { createRequestUrl } from "../Routes";
import { ScorePage } from "../ScoreTemplates/ScorePage";
import { PullToRefreshLoadingIndicator } from "./PullToRefreshLoadingIndicator";

export function Requests() {
  /********* Filters *******/
  const { flags } = useFeatureFlags();
  const {
    userInfo: { address },
  } = useAuth();
  const filtersFlag = flags?.subFilters;
  const locationFilterFlag = flags?.geocodeLinkage;
  const [ls, setLs] = useLocalStorage("swing-subs:filters", initialLocalStorageFilterValues);

  // Get user's zipcode and state from their address, with CA getting 50mi default radius
  const userZipcode = address?.zip || "";
  const userState = address?.state || "";
  const defaultRadius = userState === "CA" ? 60 : 30;

  const [locationFilter, setLocationFilter] = useLocalStorage<LocationFilterValueTypes>(
    "swing-subs:location-filter",
    {
      ...initialLocationFilterValues,
      zipcode: userZipcode,
      radius: defaultRadius,
      userZipcode: userZipcode,
      userState: userState,
      recentZipcodes: userZipcode ? [userZipcode] : [],
    },
  );

  // Ensure user's zipcode and state are always set and in recentZipcodes
  useEffect(() => {
    if (userZipcode) {
      setLocationFilter((prev) => {
        const needsUpdate =
          !prev.zipcode ||
          prev.userZipcode !== userZipcode ||
          prev.userState !== userState ||
          !prev.recentZipcodes.includes(userZipcode);

        if (!needsUpdate) return prev;

        // Ensure userZipcode is always first in recentZipcodes
        const updatedRecent = [
          userZipcode,
          ...prev.recentZipcodes.filter((z) => z !== userZipcode),
        ].slice(0, 5);

        return {
          ...prev,
          zipcode: prev.zipcode || userZipcode,
          radius: prev.radius || defaultRadius,
          userZipcode: userZipcode,
          userState: userState,
          recentZipcodes: updatedRecent,
        };
      });
    }
  }, [userZipcode, userState, defaultRadius, setLocationFilter]);

  const filtersQueryParam = filtersFlag
    ? {
        filters: JSON.stringify({
          "starting-time": ls.startingDate,
          "request-length": ls.requestLength,
          "request-type": ls.requestType,
        }),
        // Add location params when feature flag enabled and geocode is available
        ...(locationFilterFlag && { radius: locationFilter.radius }),
        ...(locationFilterFlag &&
          locationFilter.geocode && {
            geocode: JSON.stringify(locationFilter.geocode),
          }),
      }
    : undefined;

  /********* Queries ********/
  const {
    data: openings,
    isLoading: isOpeningsLoading,
    // isSuccess: isOpeningsSuccess,
    refetch: refetchOpenings,
  } = useQuery({
    queryKey: ["fetchOpenings", filtersQueryParam],
    queryFn: () =>
      GET("/api/sub/request/openings", {
        queryParams: filtersQueryParam,
      }),
    throwOnError: true, // flag necessary for uncaught errors to bubble to App level ErrorBoundary
  });

  const {
    data: confirmRequests,
    isLoading: isConfirmPendingLoading,
    // isSuccess: isConfirmPendingSuccess,
    refetch: refetchConfirmRequests,
  } = useQuery({
    queryKey: ["fetchPendingConfirm"],
    queryFn: () => GET("/api/sub/request/pending-confirm"),
    throwOnError: true, // flag necessary for uncaught errors to bubble to App level ErrorBoundary
  });

  const { data: expiringDocumentsData, isLoading: isExpiringDocumentsLoading } =
    useExpiringDocuments();
  const { documents: expiringDocuments, showBanner } = expiringDocumentsData || {
    documents: [],
    showBanner: false,
  };

  /********* Hooks ********/
  const [lastUpdatedTime, setLastUpdatedTime] = useState(DateTime.now());
  // Update the lastUpdatedTime when the app regains focus.
  // refetchOnWindowFocus is triggered automatically on web and mobile devices,
  // but we also need to update the time to make sure it's in sync.
  // If an error is thrown during the fetch, the timestamp will update,
  // but the UI will not show the time as it will be covered by the error <Alert/>
  useEffect(() => {
    const unsubscribe = focusManager.subscribe(async (isFocused) => {
      if (!isFocused) return;

      setLastUpdatedTime(DateTime.now());
      // If UI requirements change, and the timestamp should only update on successful data fetch:
      // Uncomment the following logic to manually refetch and update the time
      // only when the requests succeed. At this time, we will need to consider whether
      // refetchOnWindowFocus should be set to false.

      // const [openingsResult, confirmResult] = await Promise.all([
      //   refetchOpenings(),
      //   refetchConfirmRequests(),
      // ]);

      // if (openingsResult.status === "success" || confirmResult.status === "success") {
      //   setLastUpdatedTime(DateTime.now());
      // }
    });
    return () => unsubscribe();
  }, []);

  return (
    <ScorePage
      title={translate("PAGE_TITLE_OPENINGS")}
      isLoading={isOpeningsLoading || isConfirmPendingLoading || isExpiringDocumentsLoading}
    >
      <ContentSingleColumn>
        {showBanner && <ExpiringDocumentsBanner documents={expiringDocuments} />}
        {filtersFlag && <FilterBar ls={ls} setLs={setLs} />}
        {locationFilterFlag && (
          <div css={{ margin: "24px 16px" }}>
            <LocationFilterBar values={locationFilter} onChange={setLocationFilter} />
          </div>
        )}
        {/* TODO update with conditional for no data state when updating openings page */}
        {openings?.data && (
          <RequestsView
            requests={openings.data}
            confirmRequests={confirmRequests?.data}
            lastUpdatedTime={lastUpdatedTime}
            filters={filtersFlag ? ls : undefined}
            onRefresh={() => {
              setLastUpdatedTime(DateTime.now());
              refetchOpenings();
              refetchConfirmRequests();
            }}
          />
        )}
      </ContentSingleColumn>
    </ScorePage>
  );
}

export type RequestsViewProps = {
  requests: OpeningsData[];
  confirmRequests?: PendingConfirmData[];
  lastUpdatedTime: DateTime;
  filters?: LocalStorageFilterValueTypes;
  onRefresh: () => void;
};

export function RequestsView(props: RequestsViewProps) {
  const { requests = [], filters, confirmRequests, onRefresh, lastUpdatedTime } = props;

  /********* Hooks ********/
  const {
    userInfo: {
      isPhoneNumberVerified,
      notificationsSettings,
      hasSwingCert,
      isSwingCertRequestsAvailable,
      isActivated,
      address,
      onboardingConfigurationId,
    },
  } = useAuth();
  const { flags } = useFeatureFlags();
  const [timeSinceUpdate, setTimeSinceUpdate] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = DateTime.now();
      setTimeSinceUpdate(Math.floor(now.diff(lastUpdatedTime, "minutes").minutes));
    }, 60 * 1000); // Update every minute

    // Initial update
    setTimeSinceUpdate(Math.floor(DateTime.now().diff(lastUpdatedTime, "minutes").minutes));

    return () => clearInterval(intervalId);
  }, [lastUpdatedTime]);

  /*** State **/
  const [shouldHideSwingCertCard, setShouldHideSwingCertCard] = useState(
    () => window.localStorage.getItem("hideSwingCertCard") === "true",
  );

  /*** Constants ***/
  const showPhoneVerificationAlert = isPhoneNumberVerified === false && requests.length > 0;
  const showEnableNotificationsAlert =
    !(notificationsSettings?.push || notificationsSettings?.sms) && requests.length > 0;

  // Swing Cert Constants
  // Swing Cert applies to CA/NJ subs
  // eligibility based on state is checked on the backend
  const showNonSwingCertEmptyView = hasSwingCert === false && isSwingCertRequestsAvailable;

  const showNonSwingCertCard =
    requests.length > 0 && hasSwingCert === false && isSwingCertRequestsAvailable && isActivated;

  const refreshContainerRef = useRef<HTMLDivElement>(null);

  const subDisinterestedFlag = flags?.subDisinterestedFeedback;

  const requestsNotMarkedDisinterested = subDisinterestedFlag
    ? requests.filter((request) => !request?.hasFitFeedback)
    : requests;

  const requestsMarkedDisinterested = requests.filter((request) => request?.hasFitFeedback);

  /********* Handlers ********/
  function handlePermanentlyHidingNonSwingCertCard() {
    window.localStorage.setItem("hideSwingCertCard", "true");
    setShouldHideSwingCertCard(true);
  }

  /********* Render ********/
  return (
    <>
      {Capacitor.getPlatform() !== "web" && (
        <PullToRefreshLoadingIndicator elementRef={refreshContainerRef} onRefresh={onRefresh} />
      )}
      <div
        ref={refreshContainerRef}
        css={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          css={{
            height: "calc(100% - 48px)",
            maxWidth: "900px",
          }}
        >
          <div css={{ margin: "0 auto" }}>
            {/* Alerts */}
            {showEnableNotificationsAlert && <EnableNotificationsAlert />}
            {showPhoneVerificationAlert && <PhoneVerificationAlert />}
            {confirmRequests && <ConfirmRequests confirmRequests={confirmRequests} />}

            {/* Updated label */}
            <p css={{ margin: 16, color: Colors.slate600, fontSize: 12, fontStyle: "italic" }}>
              {translate("LABEL_UPDATED", { min: timeSinceUpdate })}
            </p>

            {/* Swing Cert Card */}
            {showNonSwingCertCard && !shouldHideSwingCertCard && (
              <div css={{ margin: "16px 16px 24px" }}>
                <NonSwingCertCard
                  onClick={handlePermanentlyHidingNonSwingCertCard}
                  state={address?.state as "CA" | "NJ"}
                  hasSwingCert={hasSwingCert}
                  onboardingConfigurationId={onboardingConfigurationId}
                  isSwingCertFeatureEnabled={!!flags.swingSubSwingCertMenuItem}
                />
              </div>
            )}

            {/* Requests List or Empty View */}
            {requests.length ? (
              <div css={{ paddingBottom: 16 }}>
                {requestsNotMarkedDisinterested.map((req) => (
                  <div key={req.id} css={{ margin: "16px 16px 24px 16px" }}>
                    <OpeningsCard request={req} />
                  </div>
                ))}
                {subDisinterestedFlag &&
                  requestsMarkedDisinterested.length > 0 &&
                  requestsNotMarkedDisinterested.length === 0 && (
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        margin: 16,
                        textAlign: "center",
                      }}
                    >
                      <img src="assets/elementary-school.png" css={{ maxWidth: "200px" }} />
                      <h3 css={{ margin: 0 }}>{translate("SUB_DISINTERESTED_EMPTY_HEADER")}</h3>
                      <p css={{ margin: "8px 0 16px 0" }}>
                        {translate("SUB_DISINTERESTED_EMPTY_BODY")}
                      </p>
                    </div>
                  )}
                {subDisinterestedFlag ? (
                  <div css={{ margin: "16px 16px 24px 16px" }}>
                    <hr css={{ border: "solid 1px var(--swing-slate200)" }} />
                    <p css={{ fontSize: 20 }}>{translate("SUB_DISINTERESTED_UNMARKED_HEADER")}</p>
                    {requestsMarkedDisinterested?.length > 0 ? (
                      requestsMarkedDisinterested.map((req) => (
                        <div key={req.id} css={{ margin: "24px 0" }}>
                          <OpeningsCard request={req} />
                        </div>
                      ))
                    ) : (
                      <div
                        css={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <img src="assets/puzzle-piece.png" css={{ maxWidth: "200px" }} />
                        <p css={{ fontWeight: "bold", margin: "8px 0 8px" }}>
                          {translate("SUB_DISINTERESTED_UNMARKED_SUBHEADER")}
                        </p>
                        <br />
                        <span>{translate("SUB_DISINTERESTED_UNMARKED_BODY")}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <></>
                )}
                <span css={{ display: "flex", justifyContent: "center", fontStyle: "italic" }}>
                  {translate("LABEL_END_OF_PAGE")}!
                </span>
              </div>
            ) : showNonSwingCertEmptyView ? (
              <NonSwingCertEmptyView
                state={address?.state as "CA" | "NJ"}
                hasSwingCert={hasSwingCert}
                onboardingConfigurationId={onboardingConfigurationId}
                isSwingCertFeatureEnabled={!!flags.swingSubSwingCertMenuItem}
              />
            ) : (
              <OpeningsEmptyView filters={filters} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

type EmptyViewProps = {
  filters?: LocalStorageFilterValueTypes;
};

/********* Helper Components ********/
export function OpeningsEmptyView(props: EmptyViewProps) {
  const { filters } = props;

  const showFilterEmptyView = filters && areFiltersSet(filters);

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--spacing-lg)",
        width: "100%",
        maxWidth: "440px",
        margin: "0 auto",
        // padding bottom to account for when confirm alert box is shown also
        padding: "104px 16px 32px 16px",
        textAlign: "center",
      }}
    >
      <img
        src={
          showFilterEmptyView
            ? "assets/woman-holding-large-pencil.png"
            : "assets/man-with-large-magnifying-glass.svg"
        }
        alt={translate("SETUP_ILLUSTRATION_IMAGE_ALT")}
        css={{ maxWidth: "200px" }}
      />
      <span css={{ fontSize: "var(--swing-font-md)", fontWeight: "var(--swing-font-semibold)" }}>
        {translate(
          showFilterEmptyView ? "FILTERED_OPENINGS_NONE_AVAILABLE" : "OPENINGS_NONE_AVAILABLE",
        )}
      </span>
      <span css={{ fontSize: "var(--swing-font-md)" }}>
        {translate(
          showFilterEmptyView ? "FILTERED_OPENINGS_RETURN_LATER" : "OPENINGS_RETURN_LATER",
        )}
        .
      </span>
    </div>
  );
}

type ConfirmRequestsProps = {
  confirmRequests: PendingConfirmData[];
};

export function ConfirmRequests(props: ConfirmRequestsProps) {
  const { confirmRequests } = props;

  return confirmRequests.map((request) => {
    const formattedStartDate = DateTime.fromISO(request.startDate).toFormat("LLL dd");
    const formattedEndDate = DateTime.fromISO(request.endDate).toFormat("LLL dd");
    const formattedExpirationDate = DateTime.fromISO(request.confirmExpirationDate).toFormat(
      "LLL, dd",
    );
    const formattedExpirationTime = DateTime.fromISO(request.confirmExpirationDate)
      .toFormat("t")
      .toLowerCase();

    return (
      <PendingConfirmationBanner
        key={request.id}
        endDate={formattedEndDate}
        expirationDate={formattedExpirationDate}
        expirationTime={formattedExpirationTime}
        schoolName={request.schoolName}
        startDate={formattedStartDate}
        url={createRequestUrl(request.id)}
      />
    );
  });
}

type PendingConfirmationBannerProps = {
  endDate: string;
  expirationDate: string;
  expirationTime: string;
  schoolName: string;
  startDate: string;
  url: string;
};

function PendingConfirmationBanner(props: PendingConfirmationBannerProps) {
  const { endDate, expirationDate, expirationTime, schoolName, startDate, url } = props;
  return (
    <div css={{ margin: 16 }}>
      <Alert showIcon color="success" title={translate("CONFIRM_REQUEST_TITLE")}>
        <p css={{ margin: 0 }}>
          {translate("CONFIRM_REQUEST_MESSAGE_1", {
            schoolName,
            startDate,
            endDate,
          })}
          {", "}
          <Link to={url}>{translate("CONFIRM_REQUEST_MESSAGE_LINK_LABEL")}</Link>{" "}
          {translate("CONFIRM_REQUEST_MESSAGE_2")}{" "}
          <span css={{ fontWeight: "var(--swing-font-semibold)" }}>{expirationDate}</span>{" "}
          {translate("CONFIRM_REQUEST_MESSAGE_3")}{" "}
          <span css={{ fontWeight: "var(--swing-font-semibold)" }}>{expirationTime}</span>.
        </p>
      </Alert>
    </div>
  );
}

export function PhoneVerificationAlert() {
  return (
    <div css={{ margin: 16 }}>
      <Alert color="warning" title={translate("PHONE_VERIFICATION_ALERT_TITLE")} showIcon>
        <p css={{ margin: 0 }}>
          <Link to="/profile">{translate("PHONE_VERIFICATION_ALERT_MSG_LINK_LABEL")}</Link>
          {` ${translate("PHONE_VERIFICATION_ALERT_MSG")}`}
        </p>
      </Alert>
    </div>
  );
}

export function EnableNotificationsAlert() {
  // Check and remove this item if it exists now that
  // we would like to always display the alert if
  // notifications is turned off.
  useEffect(() => {
    if (localStorage.getItem("notificationsAlertDismissed")) {
      localStorage.removeItem("notificationsAlertDismissed");
    }
  }, []);

  return (
    <div css={{ margin: 16 }}>
      <Alert color="warning" title={translate("NEVER_MISS_AN_OPPORTUNITY_ALERT_TITLE")} showIcon>
        <p css={{ margin: 0 }}>
          <Link to="/more/notifications">{translate("NOTIFICATIONS_ALERT_MSG_LINK_LABEL")}</Link>
          {` ${translate("NOTIFICATIONS_ALERT_MSG")}`}
        </p>
      </Alert>
    </div>
  );
}
